import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import moment from "moment";
import { useFormik } from "formik";
import * as yup from "yup";
import useStyles from "./style";
import { mapTemplateRecord } from "./mapTemplate";
import TemplatesApi from "Services/Templates";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

const PAGE_SIZE = 10;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const formatDateTime = (value) =>
  value ? moment(value).format("DD MMM YYYY, HH:mm") : "--";

const formatFileSize = (bytes) => {
  if (!bytes) return "--";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const initialFormValues = {
  name: "",
  category: "",
  language: "en",
  body_s3_key: "",
  variables: "{}",
  is_active: "true",
};

const Templates = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchTemplates = useCallback(async () => {
    dispatch(showLoader("Loading please wait..."));
    setLoading(true);

    try {
      const params = { page, limit: PAGE_SIZE };
      const trimmedSearch = searchText.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await TemplatesApi.getTemplatesList(params);
      const payload = response?.data?.data ?? {};
      const templates = Array.isArray(payload?.data) ? payload.data : [];

      setRows(templates.map(mapTemplateRecord));
      setTotalRecords(payload?.totalRecords ?? templates.length);
      setTotalPages(payload?.totalPages ?? 1);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Failed to fetch templates",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      dispatch(hideLoader());
      setLoading(false);
    }
  }, [dispatch, page, searchText]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchTemplates();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [fetchTemplates]);

  const handleCreateTemplate = useCallback(
    async (formValues, { resetForm }) => {
      let parsedVariables = {};
      try {
        parsedVariables = formValues.variables
          ? JSON.parse(formValues.variables)
          : {};
      } catch (error) {
        dispatch(
          showSnackbar({
            message: "Variables must be valid JSON",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        return;
      }

      const formData = new FormData();
      formData.append("name", formValues.name.trim());
      formData.append("category", formValues.category.trim());
      formData.append("language", formValues.language.trim() || "en");
      formData.append("body_s3_key", formValues.body_s3_key.trim());
      formData.append("variables", JSON.stringify(parsedVariables));
      formData.append("is_active", formValues.is_active);
      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      dispatch(showLoader("Loading please wait..."));

      try {
        const response = await TemplatesApi.createTemplate(formData);

        dispatch(
          showSnackbar({
            message:
              response?.data?.message || "Template created successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );

        resetForm();
        setSelectedFile(null);
        setCreateDialogOpen(false);
        setPage(1);
        fetchTemplates();
      } catch (error) {
        dispatch(
          showSnackbar({
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Error while creating template",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, fetchTemplates, selectedFile]
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: yup.object({
      name: yup.string().trim().required("Name is required"),
      category: yup.string().trim().required("Category is required"),
      language: yup.string().trim(),
      body_s3_key: yup.string().trim().required("Body S3 key is required"),
      variables: yup.string().trim(),
      is_active: yup.string().required(),
    }),
    onSubmit: handleCreateTemplate,
  });

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    formik.resetForm();
    setSelectedFile(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const filteredRows = useMemo(() => rows, [rows]);

  const activeCount = useMemo(
    () => filteredRows.filter((template) => template.isActive).length,
    [filteredRows]
  );

  return (
    <Box className={classes.page}>
      <Box className={classes.headerBar}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIconBadge}>
            <DescriptionOutlined />
          </Box>
          <Typography className={classes.headerTitle}>Templates</Typography>
          <Typography className={classes.headerCount}>{totalRecords} total</Typography>
        </Box>

        <Box className={classes.headerActions}>
          <IconButton aria-label="refresh" onClick={fetchTemplates}>
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
          <Button
            variant="contained"
            className={classes.primaryButton}
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New template
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterBar}>
        <TextField
          className={classes.searchField}
          placeholder="Search by name, category..."
          size="small"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#98A2B3" }} />
              </InputAdornment>
            ),
          }}
        />
        <Chip
          label={`${activeCount} active on this page`}
          variant="outlined"
          size="small"
        />
      </Box>

      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Name</TableCell>
              <TableCell className={classes.tableHeadCell}>Category</TableCell>
              <TableCell className={classes.tableHeadCell}>Language</TableCell>
              <TableCell className={classes.tableHeadCell}>Body S3 key</TableCell>
              <TableCell className={classes.tableHeadCell}>Status</TableCell>
              <TableCell className={classes.tableHeadCell}>File</TableCell>
              <TableCell className={classes.tableHeadCell}>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className={classes.emptyState}>
                  No templates found.
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((template) => (
                <TableRow key={template.id} hover className={classes.bodyRow}>
                  <TableCell className={classes.nameCell}>
                    <Typography className={classes.nameText} noWrap>
                      {template.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={template.category} size="small" className={classes.chip} />
                  </TableCell>
                  <TableCell>{template.language}</TableCell>
                  <TableCell>
                    <Typography className={classes.keyText} noWrap title={template.bodyS3Key}>
                      {template.bodyS3Key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.isActive ? "active" : "inactive"}
                      size="small"
                      className={`${classes.statusChip} ${
                        template.isActive ? classes.statusActive : classes.statusInactive
                      }`}
                    />
                  </TableCell>
                  <TableCell className={!template.fileName ? classes.cellMuted : undefined}>
                    {template.fileName
                      ? `${template.fileName} (${formatFileSize(template.fileSize)})`
                      : "--"}
                  </TableCell>
                  <TableCell>{formatDateTime(template.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.footerBar}>
        <Typography className={classes.footerText}>
          {totalRecords === 0
            ? "Showing 0 templates"
            : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(
                page * PAGE_SIZE,
                totalRecords
              )} of ${totalRecords} templates`}
        </Typography>

        <Box className={classes.paginationGroup}>
          <Button
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage((previous) => Math.max(1, previous - 1))}
          >
            Prev
          </Button>
          <Box className={classes.paginationPage}>{page}</Box>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
          >
            Next
          </Button>
        </Box>
      </Box>

      <Dialog
        open={createDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        aria-describedby="template-create-dialog"
      >
        <DialogTitle>
          <Box className={classes.dialogTitleContainer}>
            <Typography variant="body1" className={classes.title}>
              New Template
            </Typography>
            <IconButton aria-label="close" onClick={handleCloseDialog}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} className={classes.fieldWrapper}>
              <TextField
                name="name"
                id="name"
                label="Name *"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6} className={classes.fieldWrapper}>
              <TextField
                name="category"
                id="category"
                label="Category *"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.category && formik.errors.category)}
                helperText={formik.touched.category && formik.errors.category}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6} className={classes.fieldWrapper}>
              <TextField
                name="language"
                id="language"
                label="Language"
                value={formik.values.language}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.language && formik.errors.language)}
                helperText={formik.touched.language && formik.errors.language}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} className={classes.fieldWrapper}>
              <TextField
                name="body_s3_key"
                id="body_s3_key"
                label="Body S3 key *"
                placeholder="contracts/templates/standard-contract.html"
                value={formik.values.body_s3_key}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.body_s3_key && formik.errors.body_s3_key)}
                helperText={formik.touched.body_s3_key && formik.errors.body_s3_key}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} className={classes.fieldWrapper}>
              <TextField
                name="variables"
                id="variables"
                label="Variables (JSON)"
                value={formik.values.variables}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.variables && formik.errors.variables)}
                helperText={formik.touched.variables && formik.errors.variables}
                fullWidth
                multiline
                minRows={4}
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6} className={classes.fieldWrapper}>
              <TextField
                select
                name="is_active"
                id="is_active"
                label="Is active"
                value={formik.values.is_active}
                onChange={formik.handleChange}
                fullWidth
                variant="outlined"
                size="small"
              >
                <MenuItem value="true">true</MenuItem>
                <MenuItem value="false">false</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6} className={classes.fieldWrapper}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                className={classes.fileButton}
                startIcon={<UploadFileOutlinedIcon />}
              >
                Choisir un fichier
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              <Typography className={classes.fileNameText}>
                {selectedFile ? selectedFile.name : "Aucun fichier choisi"}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />
        <DialogActions>
          <Box className={classes.dialogActions}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              className={classes.primaryButton}
              onClick={formik.handleSubmit}
            >
              Create template
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Templates;
